import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import {
  Card, LineChart, SectionHead, StatsCards,
} from '../../PipelineComponents';
import { formattedCount, formattedPercent, STAR_ADHERENCE_THRESHOLD } from '../../../pipelineHelpers';
import WhoToCallCard from './WhoToCallCard';

const GROUP_META = {
  rasa: {
    label: 'RASA - blood pressure',
    text: 'Blood pressure medications - lisinopril, losartan, valsartan and similar.',
    note: 'These medications are cheap and generic, which is usually why this group runs ahead of the others.',
  },
  diabetes: {
    label: 'Diabetes',
    text: 'Diabetes medications - metformin, Januvia, Ozempic, Mounjaro and similar.',
    note: 'The injectables in this group are where cost and supply problems concentrate, so a shortfall here is'
      + ' more often about access than about forgetting.',
  },
  statins: {
    label: 'Statins - cholesterol',
    text: 'Cholesterol medications - atorvastatin, rosuvastatin, pravastatin.',
    note: 'Usually the biggest group, so small movements here shift more members than anywhere else.',
  },
};

const groupMeta = (groupName) => GROUP_META[String(groupName || '').toLowerCase()] || {
  label: groupName || '-',
  text: `Medications in the ${groupName || 'group'} adherence measure.`,
  note: 'Scored on the group as a whole, not on any single medication in it.',
};

const StarAdherence = ({ section, params }) => {
  const firstLoad = useRef(true);

  const [starAdherenceData, setStarAdherenceData] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getStarAdherenceSummary(params);

        setStarAdherenceData(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const cards = useMemo(() => {
    const stats = starAdherenceData?.kpis;

    return [
      {
        label: 'Members in a group',
        value: stats?.membersInGroup,
        tooltip: {
          text: 'Members taking a Diabetes, RASA or Statins medication in the selected dates.',
          steps: [
            'Counted once each, even if they are in two or three groups.',
            'Members on no rated medication are left out.',
          ],
          note: 'These are the members the health plan scores you on. Everyone else measured in section 01 still'
            + ' matters clinically, but they do not move the rating.',
        },
      },
      {
        label: `Below ${STAR_ADHERENCE_THRESHOLD}%`,
        value: stats?.belowThreshold,
        tooltip: {
          text: `Members who had their medication available less than ${STAR_ADHERENCE_THRESHOLD}% of the time.`,
          steps: [
            `${STAR_ADHERENCE_THRESHOLD}% is the line the plan uses - a member at 79% counts the same as one`
              + ' at 40%.',
            'Counted once per group, so a member short in two groups appears twice.',
          ],
          note: 'This can read higher than the members in a group above, because the same person is counted in'
            + ' each group they fall short in.',
        },
      },
      {
        label: `Can still reach ${STAR_ADHERENCE_THRESHOLD}%`,
        value: stats?.canReach,
        tooltip: {
          text: 'Members below the line who can still get back over'
            + ` ${STAR_ADHERENCE_THRESHOLD}% before 31 December.`,
          steps: [
            'Based on how many days of medication they would need against the days left in the year.',
            'Members who can no longer get there whatever happens are excluded.',
          ],
          note: 'This is the call list - the "Who to call" table below is exactly these members, easiest first.'
            + ' The rest are still worth a clinical call, but it will not change the rating this year.',
        },
      },
    ];
  }, [starAdherenceData]);

  const groups = useMemo(() => (starAdherenceData?.groups || []).map((group) => {
    const meta = groupMeta(group.groupName);

    return {
      label: meta.label,
      value: +(group.rate) || 0,
      caption: formattedPercent(group.rate),
      desc: `${formattedCount(group.adherent)} of ${formattedCount(group.members)} members`
        + ` at ${STAR_ADHERENCE_THRESHOLD}% or better`,
      tooltip: {
        text: meta.text,
        steps: [
          `${formattedCount(group.adherent)} of ${formattedCount(group.members)} members are at`
            + ` ${STAR_ADHERENCE_THRESHOLD}% or better.`,
          'A member counts once for the group, however many medications in it they take.',
        ],
        note: meta.note,
        warn: +(group.rate) < STAR_ADHERENCE_THRESHOLD,
      },
    };
  }), [starAdherenceData]);

  return (
    <>
      <SectionHead section={section} />

      <StatsCards
        firstLoad={firstLoad.current}
        loading={loading}
        cards={cards}
      />

      <div className={classes.row}>
        <Card
          title="Where each group stands"
          caption={`Share of members at ${STAR_ADHERENCE_THRESHOLD}% or better`}
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!groups.length}
        >
          <LineChart
            data={groups}
            scale="percent"
            className={classes.bordered}
          />

          <p className={classes.note}>
            The marker on each bar is the
            {' '}
            <b>{`${STAR_ADHERENCE_THRESHOLD}%`}</b>
            {' '}
            line. These three groups are the adherence measures the health plan rates, and they count
            triple — so moving Diabetes over the line is worth more than it looks.
          </p>

          <p className={classes.note}>
            Built from the same gap-day calculation as section 01 —
            {' '}
            <b>PDC = 1 − (gap days ÷ period)</b>
            {' '}
            — grouped, held to
            {' '}
            {`${STAR_ADHERENCE_THRESHOLD}%`}
            {' '}
            and fixed to the calendar year. No separate gap report is needed here.
          </p>
        </Card>
      </div>

      <WhoToCallCard params={params} />
    </>
  );
};

StarAdherence.propTypes = {
  section: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
};

export default StarAdherence;
